/**
 * Helpers for meet live stream URLs: global Meet.streamUrl + MeetPlateauStream rows.
 */

/** One per-plateau stream override (local or remote row shape). */
export type PlateauStreamLink = {
  plateauNumber: number;
  url: string;
  id?: string;
};

/** Minimal meet shape needed to resolve a live stream URL. */
export type LiveStreamMeetFields = {
  streamUrl?: string | null;
  plateauStreams?: PlateauStreamLink[] | null;
};

/** Minimal group shape for listing plateau numbers. */
export type PlateauNumberSource = {
  plateauNumber?: number | null;
};

/**
 * Builds a plateauNumber → URL map from MeetPlateauStream-like rows.
 *
 * @param streams - Plateau stream rows (empty/null → empty map).
 * @returns Map keyed by stringified plateau number.
 */
export function plateauStreamsToMap(
  streams: PlateauStreamLink[] | null | undefined,
): Record<string, string> {
  const result: Record<string, string> = {};
  if (!streams) {
    return result;
  }
  for (const row of streams) {
    if (
      row &&
      Number.isFinite(row.plateauNumber) &&
      typeof row.url === "string" &&
      row.url.trim() !== ""
    ) {
      result[String(row.plateauNumber)] = row.url.trim();
    }
  }
  return result;
}

/**
 * Converts a plateau → URL map into rows ready for upsert/replace.
 *
 * @param map - Plateau number keys → URLs.
 * @returns Cleaned PlateauStreamLink array (sorted by plateauNumber).
 */
export function mapToPlateauStreams(
  map: Record<string, string> | null | undefined,
): PlateauStreamLink[] {
  if (!map) {
    return [];
  }
  const rows: PlateauStreamLink[] = [];
  for (const [key, value] of Object.entries(map)) {
    const plateauNumber = Number(key);
    if (!Number.isFinite(plateauNumber) || typeof value !== "string") {
      continue;
    }
    const url = value.trim();
    if (!url) {
      continue;
    }
    rows.push({ plateauNumber, url });
  }
  return rows.sort((a, b) => a.plateauNumber - b.plateauNumber);
}

/**
 * Resolves the stream URL for a plateau: specific override, else global streamUrl.
 *
 * @param meet - Meet stream fields including optional plateauStreams.
 * @param plateauNumber - Selected group's plateauNumber (null → global only).
 * @returns The URL to embed, or null when none configured.
 */
export function resolveLiveStreamUrl(
  meet: LiveStreamMeetFields | null | undefined,
  plateauNumber?: number | null,
): string | null {
  if (!meet) {
    return null;
  }
  const byPlateau = plateauStreamsToMap(meet.plateauStreams);
  // Index access is `string | undefined`; narrow before returning `string | null`
  const plateauUrl =
    plateauNumber != null ? byPlateau[String(plateauNumber)] : undefined;
  if (plateauUrl) {
    return plateauUrl;
  }
  const global = meet.streamUrl?.trim();
  return global && global.length > 0 ? global : null;
}

/**
 * Lists unique plateau numbers from groups and optional existing stream rows.
 *
 * Stream rows are included so the editor still shows plateaus that already have
 * a URL even if group.plateauNumber was missing on an older remote sync.
 *
 * @param groups - Groups carrying optional plateauNumber.
 * @param streams - Optional MeetPlateauStream-like rows to union.
 * @returns Sorted unique plateau numbers.
 */
export function listMeetPlateauNumbers(
  groups: PlateauNumberSource[] | null | undefined,
  streams?: PlateauStreamLink[] | null,
): number[] {
  const set = new Set<number>();
  if (groups) {
    for (const group of groups) {
      if (group.plateauNumber != null && Number.isFinite(group.plateauNumber)) {
        set.add(group.plateauNumber);
      }
    }
  }
  if (streams) {
    for (const row of streams) {
      if (row && Number.isFinite(row.plateauNumber)) {
        set.add(row.plateauNumber);
      }
    }
  }
  return Array.from(set).sort((a, b) => a - b);
}

/**
 * True when the meet has either a global URL or at least one plateau URL.
 *
 * @param meet - Meet stream fields.
 * @returns Whether any live link is configured.
 */
export function hasAnyLiveStreamUrl(
  meet: LiveStreamMeetFields | null | undefined,
): boolean {
  if (!meet) {
    return false;
  }
  if (meet.streamUrl?.trim()) {
    return true;
  }
  return Object.keys(plateauStreamsToMap(meet.plateauStreams)).length > 0;
}
